import { cx, getStandardDateFormat } from "utilities";
import { useCreateTableColumns } from "utilities/tableColumnsUtilities/createTableColumns/createTableColumns";
import { PriorityLabel } from "../shared/components/PriorityLabel";
import { ProgressBar } from "components/miloDesignSystem/atoms/progressBar";
import { Typography } from "components/miloDesignSystem/atoms/typography";
import { OrderStatusLabel } from "../shared/components/OrderStatusLabel";
import { EmailSendingStatus } from "../shared/components/EmailSendingStatus";
import { OrderedByAvatars } from "../shared/components/OrderedByAvatars";
import { IconButton } from "components/miloDesignSystem/atoms/iconButton";
import { MdiQrCode } from "components/miloDesignSystem/atoms/icons/MdiQrCode";
import { EstimatedTimeOfSendingEmail } from "../shared/components/EstimatedTimeOfSendingEmail";
import { FulFillmentDate } from "../shared/components/FulfillmentDate";
import styles from "../shared/ExternalManufacturerPanel.module.css";
import { CreatedAtLabel } from "../shared/components/CreatedAtLabel";
import { ExternalManufacturerItem } from "api/external-manufacturing/models";
import { Tag } from "components/miloDesignSystem/atoms/tag";

export const useManufacturerItemsColumns = () => {
  return useCreateTableColumns<ExternalManufacturerItem>(({ columnHelper }) => {
    return [
      columnHelper.text(row => row.signature, {
        header: "sygnatura",
        size: 130,
        typographyProps: {
          fontSize: "14",
        },
      }),
      columnHelper.accessor(row => row.createdAt, {
        header: "utworzono",
        size: 130,
        cell: info => {
          const createdAt = info.getValue();
          return <CreatedAtLabel createdAt={createdAt} fontSize="12" fontWeight="600" />;
        },
      }),
      columnHelper.accessor(row => row.priority, {
        header: "priorytet",
        size: 56,
        cell: info => {
          const priority = info.getValue();
          return <PriorityLabel priority={priority} />;
        },
      }),
      columnHelper.accessor(row => row.numOfLineItems.total, {
        id: "numOfLineItemsTotal",
        header: () => (
          <Typography
            className="text-right w-100"
            color="neutralBlack48"
            fontSize="12"
            fontWeight="400"
          >
            l. mebli
          </Typography>
        ),
        size: 50,
        cell: info => {
          const total = info.getValue();
          return (
            <Typography className="text-right w-100" fontSize="12" fontWeight="700">
              {total}
            </Typography>
          );
        },
      }),
      columnHelper.accessor(row => row.progress, {
        header: "progres realizacji",
        size: 140,
        cell: info => {
          const progress = info.getValue();
          return (
            <div className={cx(styles.progressBarContainer, "d-flex align-items-center gap-2")}>
              <ProgressBar progress={progress} />
              <Typography fontSize="12" fontWeight="700">
                {progress}%
              </Typography>
            </div>
          );
        },
      }),
      columnHelper.accessor(row => row.orderStatus, {
        header: "status",
        size: 100,
        cell: info => {
          const status = info.getValue();
          return <OrderStatusLabel status={status} />;
        },
      }),
      columnHelper.accessor(row => row.emailSentStatus, {
        header: "status email",
        size: 100,
        cell: info => {
          const status = info.getValue();
          return <EmailSendingStatus status={status} />;
        },
      }),
      columnHelper.accessor(row => row.estimatedTimeOfSendingEmail, {
        header: "planowana wysyłka",
        size: 130,
        cell: info => {
          const date = info.getValue();
          return (
            <EstimatedTimeOfSendingEmail
              estimatedTimeOfSendingEmail={date}
              fontSize="12"
              fontWeight="600"
            />
          );
        },
      }),
      columnHelper.accessor(row => row.fulfillmentAt, {
        header: "realizacja",
        size: 130,
        cell: info => {
          const date = info.getValue();
          return <FulFillmentDate fontSize="12" fontWeight="600" fulfillmentDate={date} />;
        },
      }),
      columnHelper.accessor(
        row =>
          row.deliveredToWarehouseAt ? getStandardDateFormat(row.deliveredToWarehouseAt) : null,
        {
          header: "dostawa do magazynu",
          size: 130,
          cell: info => {
            const date = info.getValue();
            return (
              <div className="d-flex align-items-center gap-1">
                <Typography fontSize="12" fontWeight="600">
                  {date ?? "---"}
                </Typography>
                {date && <Tag label="OK" variant="success" />}
              </div>
            );
          },
        },
      ),
      columnHelper.text(
        row =>
          row.expectedWarehouseDeliveryDate
            ? getStandardDateFormat(row.expectedWarehouseDeliveryDate)
            : null,
        {
          header: "odbiór",
          size: 100,
        },
      ),
      columnHelper.accessor(row => row.orderedBy, {
        header: "zlecono przez",
        size: 130,
        cell: info => {
          const orderedBy = info.getValue();
          return <OrderedByAvatars orderedBy={orderedBy} />;
        },
      }),
      columnHelper.accessor(row => row, {
        header: "etykiety",
        size: 43,
        cell: () => {
          return (
            <div className="d-flex justify-content-center w-100">
              <IconButton
                icon={<MdiQrCode size="16" />}
                onClick={event => {
                  event.stopPropagation();
                  // TODO: download label
                }}
                variant="blackST"
              />
            </div>
          );
        },
      }),
    ];
  });
};
